import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../utils/api";
import { io } from "socket.io-client";
import LiveMap from "../components/LiveMap";
import VerticalRouteProgress from "../components/VerticalRouteProgress";
import API_BASE_URL from "../config/api";
const ParentDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [liveVehicles, setLiveVehicles] = useState({});
  const [stops, setStops] = useState([]);
  const [busPosition, setBusPosition] = useState(null);
  const [myEta, setMyEta] = useState(null);

  const selectedStudent = children[activeIndex] || null;
  const selectedVehicleId =
    selectedStudent?.vehicle_id || selectedStudent?.live_bus?.vehicle_id || null;
  const liveData = selectedVehicleId ? liveVehicles[selectedVehicleId] : null;
  const studentStopId =
    selectedStudent?.stop?.stop_id || selectedStudent?.stop_id || null;

  // Fetch static allocations
  useEffect(() => {
    const loadChildren = async () => {
      if (!user) return;
      try {
        const res = await apiFetch(`/parents/${user.user_id}/students`);
        const next = res.data || [];
        setChildren(next);
        setActiveIndex((prev) => {
          if (!next.length) return 0;
          return Math.min(prev, next.length - 1);
        });
      } catch (e) {
        console.error("Failed loading children");
      } finally {
        setLoading(false);
      }
    };
    loadChildren();
  }, [user]);

  // Fetch route stops for selected child only (and reset view state on switch)
  useEffect(() => {
    setBusPosition(null);
    setStops([]);
    setMyEta(null);

    const routeId = selectedStudent?.route_id || selectedStudent?.route?.route_id;
    if (!routeId) return;

    const fetchStops = async (rid) => {
      try {
        const res = await apiFetch(`/routes/${rid}/stops`);
        const maybe = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
            ? res.data
            : res?.data?.data;
        setStops(Array.isArray(maybe) ? maybe : []);
      } catch (err) {
        console.error("Failed to fetch stops:", err);
        setStops([]);
      }
    };

    fetchStops(routeId);
  }, [activeIndex, selectedStudent?.route_id, selectedStudent?.route?.route_id]);

  // Extract ETA specific to the selected student's stop
  useEffect(() => {
    if (!liveData || !studentStopId) {
      setMyEta(null);
      return;
    }
    if (liveData.stop_etas && Object.prototype.hasOwnProperty.call(liveData.stop_etas, studentStopId)) {
      const etaVal = liveData.stop_etas[studentStopId];
      setMyEta(etaVal !== undefined ? etaVal : null);
      return;
    }
    setMyEta(null);
  }, [liveData, studentStopId]);

  // Phase 2: Live Tracking Websocket injection
  useEffect(() => {
    const socket = io(API_BASE_URL);

    socket.on("global_gps_update", (data) => {
      console.log("GPS DATA:", data);
      setLiveVehicles((prev) => ({
        ...prev,
        [data.vehicle_id]: data,
      }));
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleLogout = async () => {
    try {
      await apiFetch("/auth/logout", { method: "POST" });
    } catch (e) {}
    logout();
    navigate("/login");
  };

  return (
    <div
      className="admin-layout"
      style={{ minHeight: "100vh", padding: "0", display: "block" }}
    >
      <header
        className="glass-panel"
        style={{
          padding: "24px 48px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderRadius: 0,
          borderBottom: "1px solid rgba(255,255,255,0.05)",
        }}
      >
        <div
          className="sidebar-brand text-gradient"
          style={{ fontSize: "1.5rem", marginBottom: 0 }}
        >
          BusTrack Parent
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "24px" }}>
          <span style={{ color: "var(--text-muted)" }}>
            Welcome,{" "}
            <strong style={{ color: "var(--text-main)" }}>
              {user?.full_name}
            </strong>
          </span>
          <button className="btn-secondary" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      <main style={{ padding: "48px", maxWidth: "1200px", margin: "0 auto" }}>
        <h2 style={{ color: "var(--text-main)", marginBottom: "32px" }}>
          Your Registered Children & Live Access Tracking
        </h2>

        {loading ? (
          <div
            style={{
              textAlign: "center",
              padding: "48px",
              color: "var(--text-muted)",
            }}
          >
            Loading records...
          </div>
        ) : children.length === 0 ? (
          <div
            className="glass-panel"
            style={{
              textAlign: "center",
              padding: "48px",
              color: "var(--text-muted)",
            }}
          >
            No children currently mapped to your account.
          </div>
        ) : (
          <>
            {children.length > 1 && (
              <div style={{ marginBottom: "20px" }}>
                <p
                  style={{
                    fontSize: "13px",
                    color: "#666",
                    marginBottom: "8px",
                  }}
                >
                  Select Child:
                </p>
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    flexWrap: "wrap",
                  }}
                >
                  {children.map((student, index) => (
                    <button
                      key={student.student_id}
                      onClick={() => setActiveIndex(index)}
                      style={{
                        padding: "8px 20px",
                        border: "2px solid #8B5A2B",
                        borderRadius: "6px",
                        background: activeIndex === index ? "#8B5A2B" : "#fff",
                        color: activeIndex === index ? "#fff" : "#000",
                        cursor: "pointer",
                        fontWeight: "500",
                        fontSize: "14px",
                      }}
                    >
                      {student.full_name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {selectedStudent ? (
              <div
                key={selectedStudent.student_id}
                className="glass-panel"
                style={{
                  padding: "24px",
                  position: "relative",
                  display: "grid",
                  gridTemplateColumns: "340px 1fr",
                  gap: "24px",
                  alignItems: "stretch",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "4px",
                    height: "100%",
                    background: selectedVehicleId
                      ? "var(--success)"
                      : "var(--accent-purple)",
                  }}
                />

                <div
                  style={{
                    paddingLeft: "8px",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: "500px",
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    <div>
                      <h3
                        style={{
                          fontSize: "1.2rem",
                          color: "var(--text-main)",
                          marginBottom: "4px",
                        }}
                      >
                        {selectedStudent.full_name}
                      </h3>
                      <span
                        style={{
                          fontSize: "0.8rem",
                          color: "var(--text-muted)",
                          textTransform: "uppercase",
                          letterSpacing: "1px",
                        }}
                      >
                        Grade {selectedStudent.grade}
                      </span>
                    </div>

                    <div
                      style={{
                        background: "rgba(0,0,0,0.05)",
                        padding: "12px",
                        borderRadius: "8px",
                        color: "var(--text-muted)",
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      <div style={{ fontSize: "14px" }}>
                        <b>Reg No:</b>{" "}
                        {selectedStudent.registration_number || "—"}
                      </div>
                      <div style={{ fontSize: "14px" }}>
                        <b>Route:</b>{" "}
                        {selectedStudent.route?.route_name ||
                          selectedStudent.route_name ||
                          "Unassigned"}
                      </div>
                      <div style={{ fontSize: "14px" }}>
                        <b>Stop:</b>{" "}
                        {selectedStudent.stop?.stop_name ||
                          selectedStudent.stop_name ||
                          "Unassigned"}
                      </div>
                      <div
                        style={{
                          padding: "15px",
                          border: "1px solid #ddd",
                          borderRadius: "8px",
                          marginTop: "10px",
                          background: "rgba(255,255,255,0.6)",
                        }}
                      >
                        <p style={{ margin: "5px 0" }}>
                          <strong>Your Stop:</strong>{" "}
                          {selectedStudent?.stop?.stop_name ||
                            selectedStudent?.stop_name ||
                            "—"}
                        </p>
                        <p style={{ margin: "5px 0" }}>
                          <strong>Next Stop:</strong>{" "}
                          {liveData?.next_stop || "—"}
                        </p>
                        <p style={{ margin: "5px 0", fontSize: "18px" }}>
                          <strong>ETA to Your Stop:</strong>{" "}
                          {myEta === 0
                            ? "Bus has arrived"
                            : myEta !== null
                              ? `${myEta} mins`
                              : "Calculating..."}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: "16px" }}>
                    <VerticalRouteProgress
                      stops={stops}
                      currentLocation={busPosition}
                    />
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column" }}>
                  {selectedVehicleId ? (
                    <div style={{ height: "500px" }}>
                      <LiveMap
                        key={selectedVehicleId || selectedStudent.student_id}
                        vehicleId={selectedVehicleId}
                        liveData={liveData}
                        stops={stops}
                        onPositionUpdate={(pos) =>
                          setBusPosition(pos ? { lat: pos[0], lng: pos[1] } : null)
                        }
                        onDisplayPositionChange={(pos) =>
                          setBusPosition(pos ? { lat: pos[0], lng: pos[1] } : null)
                        }
                      />
                    </div>
                  ) : (
                    <div
                      className="glass-panel"
                      style={{
                        height: "500px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "var(--text-muted)",
                      }}
                    >
                      No bus assigned yet
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p>No students assigned to your account</p>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default ParentDashboard;

